import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, FileText, Loader2, Save, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { GuideDownloadButton, TutorialPlayer } from "@/components/TutorialMedia";

const CHUNK_SIZE = 512 * 1024;

export const TutorialEditor = () => {
  const [draft, setDraft] = useState(null);
  const [published, setPublished] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const load = async () => {
    setLoadError(false);
    try {
      const { data } = await api.get("/site/tutorial");
      setPublished(data);
      setDraft({ video_title: data.video_title, video_description: data.video_description, video_url: data.video_url });
    } catch { setLoadError(true); }
  };
  useEffect(() => { load(); }, []);
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put("/super-admin/tutorial", draft);
      setPublished(data);
      setDraft({ video_title: data.video_title, video_description: data.video_description, video_url: data.video_url });
      toast.success("Video tutorial berhasil diperbarui.");
    } catch (err) { setError(formatApiError(err.response?.data?.detail || "Video belum tersimpan. Silakan coba lagi.")); }
    finally { setSaving(false); }
  };
  const update = (key, value) => setDraft((previous) => ({ ...previous, [key]: value }));
  if (loadError) return <div role="alert" data-testid="tutorial-editor-load-error" className="rounded-xl bg-white p-6"><p className="mb-3 text-sm text-red-800">Pengaturan tutorial belum dapat dimuat.</p><Button onClick={load} data-testid="tutorial-editor-retry">Coba Lagi</Button></div>;
  if (!draft) return <p role="status" data-testid="tutorial-editor-loading" className="flex items-center gap-2 p-6 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Memuat pengaturan tutorial...</p>;

  return <div className="space-y-6" data-testid="tutorial-editor">
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-[#27AE60]/20 bg-[#E8F6EE] p-4">
      <div><p className="text-sm font-bold text-[#0B6B3A]">Tutorial Pendaftaran</p><p className="mt-1 text-xs leading-relaxed text-[#4B5563]">Video dan panduan yang dipublikasikan dapat diakses siapa saja tanpa login.</p></div>
      <Button asChild variant="outline" className="bg-white text-xs font-bold text-[#0B6B3A]"><Link to="/tutorial" target="_blank" data-testid="tutorial-public-preview-link">Lihat Halaman <ExternalLink /></Link></Button>
    </div>
    <div className="grid items-start gap-6 xl:grid-cols-2">
      <form onSubmit={save} className="space-y-4 rounded-2xl border border-gray-100 bg-white p-5 sm:p-6" data-testid="tutorial-video-form">
        <h2 className="font-display text-base font-bold md:text-lg">Video Tutorial</h2>
        <label htmlFor="tutorial-video-title" className="block text-sm font-semibold">Judul video</label>
        <Input id="tutorial-video-title" value={draft.video_title} onChange={(e) => update("video_title", e.target.value)} required maxLength={160} disabled={saving} data-testid="tutorial-title-input" />
        <label htmlFor="tutorial-video-description" className="block text-sm font-semibold">Deskripsi singkat</label>
        <Textarea id="tutorial-video-description" value={draft.video_description} onChange={(e) => update("video_description", e.target.value)} rows={4} maxLength={1000} disabled={saving} data-testid="tutorial-description-input" />
        <label htmlFor="tutorial-video-url" className="block text-sm font-semibold">URL video</label>
        <Input id="tutorial-video-url" type="url" value={draft.video_url} onChange={(e) => update("video_url", e.target.value)} placeholder="https://www.youtube.com/watch?v=..." maxLength={2048} disabled={saving} data-testid="tutorial-url-input" aria-describedby="tutorial-url-help" />
        <p id="tutorial-url-help" className="text-xs leading-relaxed text-[#6B7280]">Gunakan tautan HTTPS YouTube atau Vimeo, bukan kode iframe. Pastikan pemilik video mengizinkan embed. Kosongkan URL untuk menyembunyikan video.</p>
        {error && <p role="alert" data-testid="tutorial-save-error" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        <Button type="submit" disabled={saving} data-testid="tutorial-save-btn" className="h-11 rounded-xl bg-[#0B6B3A] font-bold hover:bg-[#0B3D1F]">{saving ? <Loader2 className="animate-spin" /> : <Save />}{saving ? "Menyimpan..." : "Simpan Video"}</Button>
      </form>
      <GuideUploader guide={published.guide} onUploaded={(guide) => setPublished((previous) => ({ ...previous, guide }))} />
    </div>
    <div className="max-w-2xl rounded-2xl border border-gray-100 bg-white p-5 sm:p-6">
      <h2 className="mb-4 font-display text-base font-bold md:text-lg">Pratinjau Video Tersimpan</h2>
      <TutorialPlayer embedUrl={published.embed_url} title={published.video_title} testId="tutorial-admin-video-embed" />
    </div>
  </div>;
};

const GuideUploader = ({ guide, onUploaded }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const upload = async (file) => {
    if (!file) return;
    setError("");
    setSuccess("");
    if (!file.name.toLowerCase().endsWith(".pdf") || !file.size || file.size > 10 * 1024 * 1024) {
      setError("Pilih berkas PDF berukuran lebih dari 0 byte dan maksimal 10 MB.");
      return;
    }
    setUploading(true);
    setProgress(0);
    setPhase(`Menyiapkan ${file.name}`);
    try {
      let uploadId;
      const count = Math.ceil(file.size / CHUNK_SIZE);
      for (let index = 0; index < count; index += 1) {
        const start = index * CHUNK_SIZE;
        const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
        const form = new FormData();
        form.append("file", chunk, file.name);
        form.append("file_size", String(file.size));
        form.append("chunk_index", String(index));
        if (uploadId) form.append("upload_id", uploadId);
        setPhase(`Mengunggah bagian ${index + 1} dari ${count}`);
        const { data } = await api.post("/super-admin/tutorial/guide", form, {
          onUploadProgress: (event) => {
            const ratio = event.total ? event.loaded / event.total : 0;
            setProgress(Math.min(95, Math.round(((start + chunk.size * ratio) / file.size) * 95)));
            if (index === count - 1 && ratio === 1) setPhase("Memvalidasi dan menyimpan PDF...");
          },
        });
        uploadId = data.upload_id;
        if (data.complete) {
          onUploaded(data.tutorial.guide);
          setProgress(100);
          setSuccess("PDF berhasil dipublikasikan. Pengunjung kini dapat mengunduh panduan terbaru.");
          toast.success("Buku panduan berhasil diperbarui.");
        }
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail || "Unggahan gagal. Panduan sebelumnya tetap tersedia. Silakan coba lagi."));
    } finally { setUploading(false); }
  };
  return <section className="space-y-4 rounded-2xl border border-gray-100 bg-white p-5 sm:p-6" data-testid="tutorial-guide-editor" aria-busy={uploading}>
    <h2 className="font-display text-base font-bold md:text-lg">Buku Panduan PDF</h2>
    <p className="text-sm leading-relaxed text-[#6B7280]">Unggah PDF maksimal 10 MB. Setelah unggahan selesai, berkas langsung dipublikasikan dan menggantikan panduan sebelumnya.</p>
    {guide ? <div className="rounded-xl bg-[#F9FAFB] p-4"><div className="mb-4 flex items-start gap-3"><FileText className="h-5 w-5 shrink-0 text-[#0B6B3A]" /><p className="break-all text-sm font-semibold" data-testid="tutorial-admin-guide-filename">{guide.filename}</p></div><GuideDownloadButton guide={guide} testId="tutorial-admin-download-btn" className="bg-[#0B6B3A] hover:bg-[#0B3D1F]" /></div>
      : <p className="rounded-xl border border-dashed border-gray-200 p-5 text-sm text-[#6B7280]" data-testid="tutorial-admin-guide-empty">Belum ada buku panduan yang diunggah.</p>}
    <input ref={inputRef} type="file" accept=".pdf,application/pdf" className="hidden" disabled={uploading} data-testid="tutorial-pdf-input" onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; upload(file); }} />
    <Button type="button" variant="outline" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid="tutorial-upload-btn" className="h-11 rounded-xl font-bold text-[#0B6B3A]">{uploading ? <Loader2 className="animate-spin" /> : <UploadCloud />}{uploading ? "Mengunggah..." : guide ? "Ganti PDF Panduan" : "Unggah PDF Panduan"}</Button>
    {uploading && <div role="status" aria-live="polite" data-testid="tutorial-upload-progress"><p className="mb-2 break-words text-xs text-[#0B6B3A]">{phase} · {progress}%</p><progress value={progress} max={100} className="h-2 w-full accent-[#27AE60]" aria-label="Progres unggahan PDF" /></div>}
    {error && <p role="alert" data-testid="tutorial-upload-error" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    {success && <p role="status" data-testid="tutorial-upload-success" className="rounded-lg bg-[#E8F6EE] p-3 text-sm text-[#0B6B3A]">{success}</p>}
  </section>;
};
